import logError from "./logError";
import { log, LogCategory } from "./log";

// Device-level events
export type DeviceEvents =
  | "registered"
  | "unregistered"
  | "error"
  | "inbound"
  | "outbound"
  | "reconnection"
  | "transcription"
  | "text-to-speech"
  | "change_room"
  | "change_param";

// Call-specific events
export type CallEvents =
  | "call.accept"
  | "call.reject"
  | "call.disconnect"
  | "call.cancel"
  | "call.reconnecting"
  | "call.reconnected"
  | "call.ringing"
  | "call.warning"
  | "call.mute"
  | "call.sendDigits"
  | "call.error"
  | "call.miss";

// Combined event types
export type Events = DeviceEvents | CallEvents;

const MAX_RECONNECTION_COUNT = 5;

type SocketReadyStatus = "CONNECTING" | "OPEN" | "CLOSING" | "CLOSED" | "unknown";

export type ResponseType = {
  event: Events;
  data?: any;
};

type EventCallback = (...args: any[]) => void;

type CallParameters = {
  From: string;
  To: string;
  CallSid: string;
  Direction: string;
};

const logE = (event: string, ...args: any[]) => log(LogCategory.IpPhone, "##### " + event, ...args);

export class Call {
  private eventListeners: Map<string, EventCallback> = new Map();
  public parameters: CallParameters;
  private device: Device;

  constructor(device: Device, parameters: CallParameters) {
    this.device = device;
    this.parameters = parameters;
  }

  /**
   * Register an event listener
   * @param event Event name to listen for
   * @param callback Callback function to execute when the event occurs
   */
  public on(event: CallEvents, callback: (data?: any) => void): this {
    // logE("Registering event listener for call: ", event);
    if (!this.eventListeners.has(event)) {
      this.eventListeners.set(event, callback);
    } else {
      this.off(event);
      this.eventListeners.set(event, callback);
    }

    return this;
  }

  /**
   * Remove an event listener
   * @param event Event name
   */
  public off(event: CallEvents): this {
    // logE("Removing event listener for call: ", event);
    this.eventListeners.delete(event);
    return this;
  }

  /**
   * Trigger an event to all registered listeners
   * @param event Event name
   * @param args Arguments to pass to the listeners
   */
  public triggerEvent(event: CallEvents, ...args: any[]): void {
    logE("Triggering event for call: ", event, args);
    const callback = this.eventListeners.get(event);
    if (callback) {
      try {
        callback(...args);
      } catch (error) {
        logError(`Error in call event listener for ${event}`, error);
      }
    } else {
      logE(`No listeners found for event in call: ${event}`);
    }

    if (event === "call.disconnect") {
      this.device.removeCall(this.parameters.CallSid);
    }
  }

  /**
   * Disconnect the call
   */
  public disconnect(): void {
    logE("Disconnecting call: ", this.parameters.CallSid);
    // Send disconnect message to server
    this.device.sendEvent("call.disconnect", { call_id: this.parameters.CallSid });
  }

  /**
   * Accept the call
   */
  public accept(): void {
    logE("Accepting call: ", this.parameters.CallSid);
    // Send accept message to server
    this.device.sendEvent("call.accept", { call_id: this.parameters.CallSid });
  }

  /**
   * Cancel the call
   */
  public cancel(): void {
    logE("Canceling call: ", this.parameters.CallSid);
    // Send cancel message to server
    this.device.sendEvent("call.cancel", { call_id: this.parameters.CallSid });
  }

  /**
   * Reject the call
   */
  public reject(): void {
    logE("Rejecting call: ", this.parameters.CallSid);
    // Send reject message to server
    this.device.sendEvent("call.reject", { call_id: this.parameters.CallSid });
  }

  /**
   * Mute or unmute the call
   * @param isMuted Whether the call should be muted
   */
  public mute(isMuted: boolean): void {
    logE("Muting call: ", this.parameters.CallSid, isMuted);
    // Send mute message to server
    this.device.sendEvent("call.mute", { call_id: this.parameters.CallSid, muted: isMuted });
  }

  /**
   * Send DTMF digits
   * @param digits Digits to send
   */
  public sendDigits(digits: string): void {
    logE("Sending DTMF digits: ", this.parameters.CallSid, digits);
    // Send digits message to server
    this.device.sendEvent("call.sendDigits", { call_id: this.parameters.CallSid, digits });
  }
}

export class Device {
  private socket: WebSocket | null = null;
  private url: string = "";
  private token: string = "";
  private reconnectionInterval: NodeJS.Timeout | null = null;
  private reconnectionCount = 0;
  private eventListeners: Map<string, EventCallback> = new Map();
  private isDisconnecting: boolean = false;
  private activeCall: Call | null = null;
  private transcriptionLanguage: string | undefined;

  constructor() {
    this.socket = null;
    this.eventListeners = new Map();
  }

  public connect = (url: string, token: string) => {
    try {
      if (
        this.url === url &&
        this.token === token &&
        (this.getSocketReadyStatus() === "OPEN" || this.getSocketReadyStatus() === "CONNECTING")
      ) {
        logE(`Already ${this.getSocketReadyStatus()} to ${url}, so refuse your connect request.`);
        return this;
      }

      this.url = url;

      url = url + "/ws";

      const queryParams = [];
      if (this.transcriptionLanguage) {
        queryParams.push(`transcriptionLanguage=${this.transcriptionLanguage}`);
      }

      // Add query parameters to URL if any exist
      if (queryParams.length > 0) {
        url += `?${queryParams.join("&")}`;
      }

      logE(`connecting to ${url} [${this.reconnectionCount}]`);
      this.token = token;

      this.socket?.close();
      this.socket = null;

      this.socket = new WebSocket(url, [`jwt-token-${token}`]);
      this.socket.onopen = () => {
        logE("onopen", url);
        this.clearReconnectionInterval();
        this.reconnectionCount = 0;
      };

      this.socket.onclose = event => {
        logE("onclose", event);

        // Trigger disconnect event
        this.triggerEvent("unregistered", event);

        this.setReconnectionInterval();
        this.isDisconnecting = false;
      };

      this.socket.onerror = event => {
        logE("onerror", event);

        // Trigger error event
        this.triggerEvent("error", event);

        this.setReconnectionInterval();
        this.isDisconnecting = false;
      };

      this.socket.onmessage = event => {
        try {
          const data = JSON.parse(event.data);
          this.onResponseWrapper(data);
        } catch (e) {
          logError("Failed to parse message", e);
          this.triggerEvent("error", e);
        }
      };

      return this;
    } catch (e) {
      logError("Failed to connect", e);
      this.triggerEvent("error", e);
      return this;
    }
  };

  public disconnect = () => {
    logE("disconnect");
    this.isDisconnecting = true;
    this.activeCall = null;
    this.clearReconnectionInterval();

    if (this.socket) {
      this.sendEvent("unregistered");
      this.socket.close();
      this.socket = null;
    } else {
      this.isDisconnecting = false;
    }
  };

  public updateTranscriptionLanguage = (transcriptionLanguage: string) => {
    this.transcriptionLanguage = transcriptionLanguage;
    this.sendEvent("transcription", { transcriptionLanguage });
  };

  /**
   * Make an outbound call
   * @param phoneNumber Phone number to call
   * @param options Additional options for the call
   */
  public makeCall = (phoneNumber: string, options: any = {}) => {
    logE("Making outbound call: ", phoneNumber, options);
    const timeout = options.timeout || 60;

    // Create call parameters similar to Twilio format
    const parameters: CallParameters = {
      To: phoneNumber,
      From: options.from || "",
      CallSid: "",
      Direction: "outbound",
    };

    // Create a new Call object
    const call = new Call(this, parameters);

    // Store the call in our active call
    this.activeCall = call;

    // Send the outbound call request to the server
    this.sendEvent("outbound", {
      to_number: phoneNumber,
      timeout: timeout,
      ...options,
    });

    return call;
  };

  /**
   * Register the device with the server
   */
  public register = () => {
    // For compatibility with Twilio Device API
    // The actual registration happens during connect
    this.triggerEvent("registered");
  };

  /**
   * Destroy the device and clean up resources
   */
  public destroy = () => {
    logE("Destroying device");
    this.disconnect();
    this.eventListeners.clear();
  };

  public onResponseWrapper = (response: ResponseType) => {
    logE("onResponseWrapper: ", response);

    let data: any;
    try {
      data = JSON.parse(response.data);
    } catch (error) {
      data = response.data;
    }

    // Handle incoming call events
    if (response.event === "inbound") {
      // Create a new call object for the incoming call
      const parameters: CallParameters = {
        From: data.from || "Unknown",
        To: data.to || "You",
        CallSid: data.call_id,
        Direction: "inbound",
      };

      if (this.activeCall) {
        console.error("Active call already exists", parameters);
        return;
      }

      const call = new Call(this, parameters);
      this.activeCall = call;

      // Trigger the incoming event with the call object
      this.triggerEvent("inbound", call);
      return;
    }

    // Handle reconnection event
    if (response.event === "reconnection") {
      const parameters: CallParameters = {
        From: data.from || "Unknown",
        To: data.to || "You",
        CallSid: data.call_id,
        Direction: data.direction,
      };

      if (this.activeCall) {
        console.error("Active call already exists", parameters);
        return;
      }

      const call = new Call(this, parameters);
      this.activeCall = call;

      // Trigger the incoming event with the call object
      this.triggerEvent("reconnection", call);
      return;
    }

    // Handle other call-specific events (prefixed with call.)
    if (response.event.startsWith("call.")) {
      const callId = data.call_id;
      logE("call event: ", response.event, callId, this.activeCall);

      if (this.activeCall && this.activeCall.parameters.CallSid === "" && callId) {
        this.activeCall.parameters.CallSid = callId;
      }

      if (this.activeCall && this.activeCall.parameters.CallSid === callId) {
        this.activeCall.triggerEvent(response.event as CallEvents, data);
      }
      return;
    }

    // For any other events, just trigger them directly
    this.triggerEvent(response.event, data);
  };

  /**
   * Register an event listener
   * @param event Event name to listen for
   * @param callback Function to call when the event occurs
   */
  public on(event: Events, callback: EventCallback): this {
    // logE("Registering event listener for device: ", event);
    if (!this.eventListeners.has(event)) {
      this.eventListeners.set(event, callback);
    } else {
      this.off(event);
      this.eventListeners.set(event, callback);
    }

    return this;
  }

  /**
   * Remove an event listener
   * @param event Event name
   */
  public off(event: Events): this {
    // logE("Removing event listener for device: ", event);
    this.eventListeners.delete(event);
    return this;
  }

  /**
   * Trigger an event to all registered listeners
   * @param event Event name
   * @param args Arguments to pass to the listeners
   */
  private triggerEvent(event: Events, ...args: any[]): void {
    logE("Triggering event for device: ", event, args);
    const callback = this.eventListeners.get(event);
    if (callback) {
      try {
        callback(...args);
      } catch (error) {
        logError(`Error in event listener for ${event}`, error);
      }
    } else {
      logE(`No listeners found for event in device: ${event}`);
    }
  }

  /**
   * Send an event to the server
   * @param event Event name
   * @param data Data to send with the event
   */
  public sendEvent(event: Events, data?: any) {
    logE("Sending event to server: ", event, data);
    try {
      if (data instanceof ArrayBuffer) {
        if (this.socket && this.socket.readyState === WebSocket.OPEN) {
          this.socket.send(data);
        }
      } else {
        const message = JSON.stringify({
          event,
          data,
        });

        if (this.socket && this.socket.readyState === WebSocket.OPEN) {
          this.socket.send(message);
        }
      }
    } catch (e) {
      logError("Failed to emit: ", e);
    }
  }

  public setReconnectionInterval = () => {
    if (
      this.isDisconnecting ||
      this.reconnectionInterval ||
      this.getSocketReadyStatus() === "OPEN" ||
      this.getSocketReadyStatus() === "CONNECTING"
    )
      return;

    logE(`setReconnectionInterval (${this.getSocketReadyStatus()})`);

    this.reconnectionInterval = setInterval(() => {
      if (this.getSocketReadyStatus() === "OPEN") {
        this.clearReconnectionInterval();
        return;
      }

      this.reconnectionCount += 1;

      if (this.reconnectionCount > MAX_RECONNECTION_COUNT) {
        logE("Exceeded max reconnection count and stopped reconnection", MAX_RECONNECTION_COUNT);
        this.clearReconnectionInterval();
      } else {
        this.connect(this.url, this.token);
      }
    }, 10_000);
  };

  public clearReconnectionInterval = () => {
    if (this.reconnectionInterval) {
      logE(`clearReconnectionInterval (${this.getSocketReadyStatus()})`);

      clearInterval(this.reconnectionInterval);
      this.reconnectionInterval = null;
    }

    this.reconnectionCount = 0;
  };

  public getSocketReadyStatus = (): SocketReadyStatus => {
    switch (this.socket?.readyState) {
      case 0:
        return "CONNECTING";
      case 1:
        return "OPEN";
      case 2:
        return "CLOSING";
      case 3:
        return "CLOSED";
      default:
        return "unknown";
    }
  };

  public removeCall(callId: string): void {
    logE("Removing call from active calls: ", callId);
    if (this.activeCall && this.activeCall.parameters.CallSid === callId) {
      this.activeCall = null;
    }
  }
}

export function isSipParticipant(identity: string) {
  return identity.startsWith("sip_") || identity.startsWith("agent_");
}
